import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DepartmentsPage = () => {
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/departments`, { withCredentials: true });
      setDepartments(res.data);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/departments`, { name }, { withCredentials: true });
      setOpen(false);
      setName('');
      loadDepartments();
    } catch (error) {
      console.error('Error creating department:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/api/departments/${id}`, { withCredentials: true });
      loadDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? departments.filter((d) => (d.name || '').toLowerCase().includes(q)) : departments;
  }, [departments, search]);
  const { paged, controls, startIdx } = usePagination(filtered, { storageKey: 'departments-page-size', testIdPrefix: 'department' });

  return (
    <div data-testid="departments-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Departments
          </h1>
          <p className="text-[#52525B] text-sm">Manage organizational departments</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-department-btn"
              className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white rounded-sm"
            >
              <Plus size={20} className="mr-2" />
              Add Department
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Department</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Department Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="e.g., Engineering, HR, Finance"
                  data-testid="department-name-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1D9E75] hover:bg-[#0F6E56]" data-testid="save-department-btn">
                  Save
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-[#E4E4E7]">
        <div className="p-3 border-b">
          <Input
            placeholder="Search department name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
            data-testid="department-search"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F5]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">#</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Department Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-[#52525B]">
                  {departments.length === 0 ? 'No departments found. Add your first department.' : 'No matches.'}
                </TableCell>
              </TableRow>
            ) : paged.map((dept, idx) => (
              <TableRow key={dept.id} data-testid={`department-row-${dept.id}`}>
                <TableCell className="text-gray-400">{startIdx + idx + 1}</TableCell>
                <TableCell className="font-medium">{dept.name}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[#DC2626] hover:text-[#DC2626]"
                    onClick={() => handleDelete(dept.id)}
                    data-testid={`delete-department-${dept.id}`}
                  >
                    <Trash size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {controls}
      </Card>
    </div>
  );
};
